/**
 * Client-side search over the page registries: "What do you want to do?" and the sidebar's
 * "Search pages and tasks" (Ctrl K). No server call.
 *
 * Ranking, per item:
 *   - every meaningful word of the query must match somewhere (title, synonyms, crumb/section/group,
 *     help), as the start of a word or, for words of 4+ letters, anywhere inside one;
 *   - each word scores by the best field it hit: title 10, synonym 8, section / group / crumb 4,
 *     help 2, with +2 when it hit the start of a word;
 *   - the whole query as a phrase adds: title equal 40, title starts with it 25, title contains
 *     it 15, a synonym contains it 12;
 *   - common tasks win ties over pages (they are phrased the way people ask).
 * Results sort by score, then shorter title, then A-Z.
 */

import type { SearchItem, ShellNav, ShellTask } from './types';

const STOP_WORDS = new Set([
  'a', 'an', 'the', 'to', 'of', 'for', 'in', 'on', 'at', 'by', 'and', 'or', 'i', 'me', 'my', 'we', 'our',
  'want', 'wanna', 'need', 'how', 'do', 'does', 'can', 'where', 'is', 'are', 'what', 'which', 'please', 'someone',
]);

export function normalize(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[‘’`']/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** The words of a query that carry meaning (stop words dropped unless that leaves nothing). */
export function queryWords(query: string): string[] {
  const all = normalize(query).split(' ').filter(Boolean);
  const kept = all.filter((w) => !STOP_WORDS.has(w));
  return kept.length > 0 ? kept : all;
}

/** 2 = a word starts with `w`, 1 = some word contains it (4+ letters only), 0 = no match. */
function wordHit(field: string, w: string): 0 | 1 | 2 {
  if (!field) return 0;
  const words = field.split(' ');
  if (words.some((x) => x.startsWith(w))) return 2;
  if (w.length >= 4 && words.some((x) => x.includes(w))) return 1;
  return 0;
}

export interface ScoredItem<T extends SearchItem = SearchItem> {
  item: T;
  score: number;
}

/** Score one item against a query; 0 means it does not match. */
export function scoreItem(item: SearchItem, query: string): number {
  const words = queryWords(query);
  if (words.length === 0) return 0;
  const title = normalize(item.title);
  const syns = (item.synonyms ?? []).map(normalize);
  const where = normalize([item.crumb, item.section ?? '', item.group ?? ''].join(' '));
  const help = normalize(item.help ?? '');

  let score = 0;
  for (const w of words) {
    let best = 0;
    const t = wordHit(title, w);
    if (t) best = Math.max(best, 10 + (t === 2 ? 2 : 0));
    for (const s of syns) {
      const h = wordHit(s, w);
      if (h) best = Math.max(best, 8 + (h === 2 ? 2 : 0));
    }
    const c = wordHit(where, w);
    if (c) best = Math.max(best, 4 + (c === 2 ? 2 : 0));
    const hh = wordHit(help, w);
    if (hh) best = Math.max(best, 2 + (hh === 2 ? 2 : 0));
    if (best === 0) return 0; // every word must match somewhere
    score += best;
  }

  const phrase = words.join(' ');
  if (title === phrase) score += 40;
  else if (title.startsWith(phrase)) score += 25;
  else if (title.includes(phrase)) score += 15;
  else if (syns.some((s) => s.includes(phrase))) score += 12;
  return score;
}

/** Ranked matches, best first. An empty query returns nothing. */
export function searchItems<T extends SearchItem>(items: ReadonlyArray<T>, query: string, limit = 8): T[] {
  if (!query.trim()) return [];
  const scored: ScoredItem<T>[] = [];
  for (const item of items) {
    const score = scoreItem(item, query) + (item.kind === 'task' ? 0.5 : 0);
    if (score > 0.5) scored.push({ item, score });
  }
  scored.sort(
    (a, b) =>
      b.score - a.score ||
      a.item.title.length - b.item.title.length ||
      a.item.title.localeCompare(b.item.title),
  );
  // One result per destination: a task and its page often point at the same address.
  const seen = new Set<string>();
  const out: T[] = [];
  for (const s of scored) {
    const key = `${s.item.kind}|${s.item.path}|${s.item.title}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(s.item);
    if (out.length >= limit) break;
  }
  return out;
}

/** Search items for every visible page of a nav (prefix: e.g. "Retail" so cross-platform results say where they are). */
export function pagesToSearchItems(nav: ShellNav, prefix?: string): SearchItem[] {
  const items: SearchItem[] = [];
  const head = prefix ?? nav.root.label;
  for (const s of nav.sections) {
    for (const pg of s.pages) {
      items.push({
        kind: 'page',
        title: pg.label,
        path: pg.path,
        crumb: [head, s.label, pg.group && pg.group !== pg.label ? pg.group : null].filter(Boolean).join(' › '),
        section: s.label,
        group: pg.group,
        help: pg.help,
        synonyms: pg.synonyms,
        external: pg.external,
      });
    }
  }
  return items;
}

export function tasksToSearchItems(tasks: ReadonlyArray<ShellTask>, prefix?: string): SearchItem[] {
  return tasks.map((t) => ({
    kind: 'task' as const,
    title: t.title,
    path: t.path,
    crumb: prefix ? `${prefix} › ${t.crumb}` : t.crumb,
    help: t.blurb,
    synonyms: t.synonyms,
    external: t.external,
  }));
}
